import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  ApproveEnrollmentDTO,
  BackendVersionInfo,
  CreateAnnouncementDTO,
  CreateMenuProductDTO,
  EmergencyAnnouncementDTO,
  SignageBestsellers,
  SignageDaypartRule,
  SignageDaypartRuleInput,
  SignageAnnouncement,
  SignageDisplay,
  SignageDisplaySettings,
  SignageEnrollment,
  SignageMenuConfig,
  SignageMenuProduct,
  SignagePlaylist,
  SignagePlaylistItem,
  UpdateDisplayDTO,
  UpdateMenuProductDTO,
  signageClient,
} from '@/api/signageClient';

export const signageQueryKeys = {
  displays: ['signage', 'displays'] as const,
  display: (id: string) => ['signage', 'displays', id] as const,
  displayCache: (id: string) => ['signage', 'displays', id, 'cache'] as const,
  enrollments: (status = 'pending') =>
    ['signage', 'enrollments', status] as const,
  backendVersion: ['version', 'backend'] as const,
  announcements: ['signage', 'announcements'] as const,
  playlists: ['signage', 'playlists'] as const,
  playlistItems: (playlistId: string) =>
    ['signage', 'playlists', playlistId, 'items'] as const,
  playlistSchedule: (playlistId: string) =>
    ['signage', 'playlists', playlistId, 'schedule'] as const,
  bestsellers: (branchId?: string) =>
    ['signage', 'menu-bestsellers', branchId] as const,
  menuConfig: (branchId?: string) =>
    ['signage', 'menu-config', branchId] as const,
  menuProducts: (branchId?: string) =>
    ['signage', 'menu-products', branchId] as const,
  settings: (branchId?: string) => ['signage', 'settings', branchId] as const,
};

export function useSignageDisplays() {
  return useQuery({
    queryKey: signageQueryKeys.displays,
    queryFn: () => signageClient.listDisplays(),
    staleTime: 30_000,
  });
}

export function useSignageAnnouncements() {
  return useQuery({
    queryKey: signageQueryKeys.announcements,
    queryFn: () => signageClient.listAnnouncements(),
    staleTime: 15_000,
  });
}

export function useCreateAnnouncement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateAnnouncementDTO) =>
      signageClient.createAnnouncement(data),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: signageQueryKeys.announcements }),
  });
}

export function useUpdateAnnouncement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Partial<CreateAnnouncementDTO> & { active?: boolean };
    }) => signageClient.updateAnnouncement(id, data),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: signageQueryKeys.announcements }),
  });
}

export function useDeleteAnnouncement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => signageClient.deleteAnnouncement(id),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: signageQueryKeys.announcements }),
  });
}

export function usePublishAnnouncement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => signageClient.publishAnnouncement(id),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: signageQueryKeys.announcements }),
  });
}

export function useEmergencyAnnouncement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: EmergencyAnnouncementDTO) =>
      signageClient.emergencyAnnouncement(data),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: signageQueryKeys.announcements }),
  });
}

export function useClearEmergency() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => signageClient.clearEmergency(),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: signageQueryKeys.announcements }),
  });
}

export function useSignagePlaylists() {
  return useQuery({
    queryKey: signageQueryKeys.playlists,
    queryFn: () => signageClient.listPlaylists(),
    staleTime: 30_000,
  });
}

export function useCreatePlaylist() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { name: string; branch_id?: string }) =>
      signageClient.createPlaylist(data),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: signageQueryKeys.playlists }),
  });
}

export function useUpdatePlaylist() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: { name?: string; active?: boolean };
    }) => signageClient.updatePlaylist(id, data),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: signageQueryKeys.playlists }),
  });
}

export function useDeletePlaylist() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => signageClient.deletePlaylist(id),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: signageQueryKeys.playlists }),
  });
}

export function usePublishPlaylist() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => signageClient.publishPlaylist(id),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: signageQueryKeys.playlists }),
  });
}

export function usePlaylistItems(playlistId: string | null | undefined) {
  return useQuery({
    queryKey: signageQueryKeys.playlistItems(playlistId ?? ''),
    queryFn: () => signageClient.listPlaylistItems(playlistId as string),
    enabled: Boolean(playlistId),
    staleTime: 15_000,
  });
}

export function useUpdatePlaylistItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      playlistId,
      itemId,
      data,
    }: {
      playlistId: string;
      itemId: string;
      data: { caption?: string; duration_sec?: number; display_order?: number };
    }) => signageClient.updatePlaylistItem(playlistId, itemId, data),
    onSuccess: (_data, { playlistId }) => {
      qc.invalidateQueries({
        queryKey: signageQueryKeys.playlistItems(playlistId),
      });
    },
  });
}

export function useRemovePlaylistItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      playlistId,
      itemId,
    }: {
      playlistId: string;
      itemId: string;
    }) => signageClient.removePlaylistItem(playlistId, itemId),
    onSuccess: (_data, { playlistId }) => {
      qc.invalidateQueries({
        queryKey: signageQueryKeys.playlistItems(playlistId),
      });
    },
  });
}

export function usePlaylistSchedule(playlistId: string | null | undefined) {
  return useQuery({
    queryKey: signageQueryKeys.playlistSchedule(playlistId ?? ''),
    queryFn: () => signageClient.getPlaylistSchedule(playlistId as string),
    enabled: Boolean(playlistId),
    staleTime: 15_000,
  });
}

export function useUpdatePlaylistSchedule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      playlistId,
      schedule,
    }: {
      playlistId: string;
      schedule: SignageDaypartRuleInput[];
    }) => signageClient.updatePlaylistSchedule(playlistId, schedule),
    onSuccess: (_data, { playlistId }) => {
      qc.invalidateQueries({
        queryKey: signageQueryKeys.playlistSchedule(playlistId),
      });
    },
  });
}

export function useMenuBestsellers(branchId?: string) {
  return useQuery({
    queryKey: signageQueryKeys.bestsellers(branchId),
    queryFn: () => signageClient.getMenuBestsellers(branchId),
    staleTime: 60_000,
  });
}

export function useSignageMenuConfig(branchId?: string) {
  return useQuery({
    queryKey: signageQueryKeys.menuConfig(branchId),
    queryFn: () => signageClient.getMenuConfig(branchId),
    staleTime: 60_000,
  });
}

export function useUpdateMenuConfig(branchId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<SignageMenuConfig>) =>
      signageClient.updateMenuConfig(data, branchId),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: signageQueryKeys.menuConfig(branchId) }),
  });
}

// ─── Menu Products (Phase 3 follow-up — real admin-entered catalog) ─────────

export function useMenuProducts(branchId?: string) {
  return useQuery({
    queryKey: signageQueryKeys.menuProducts(branchId),
    queryFn: () => signageClient.listMenuProducts(branchId),
    staleTime: 15_000,
  });
}

export function useCreateMenuProduct(branchId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateMenuProductDTO) =>
      signageClient.createMenuProduct(data, branchId),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: signageQueryKeys.menuProducts(branchId),
      });
      qc.invalidateQueries({
        queryKey: signageQueryKeys.bestsellers(branchId),
      });
    },
  });
}

export function useUpdateMenuProduct(branchId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateMenuProductDTO }) =>
      signageClient.updateMenuProduct(id, data),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: signageQueryKeys.menuProducts(branchId),
      });
      qc.invalidateQueries({
        queryKey: signageQueryKeys.bestsellers(branchId),
      });
    },
  });
}

export function useDeleteMenuProduct(branchId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => signageClient.deleteMenuProduct(id),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: signageQueryKeys.menuProducts(branchId),
      });
      qc.invalidateQueries({
        queryKey: signageQueryKeys.bestsellers(branchId),
      });
    },
  });
}

export function useSignageSettings(branchId?: string) {
  return useQuery({
    queryKey: signageQueryKeys.settings(branchId),
    queryFn: () => signageClient.getDisplaySettings(branchId),
    staleTime: 60_000,
  });
}

export function useUpdateDisplaySettings(branchId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<SignageDisplaySettings>) =>
      signageClient.updateDisplaySettings(data, branchId),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: signageQueryKeys.settings(branchId) }),
  });
}

// ─── Device enrollment ───────────────────────────────────────────────────────

/** Pending device enrollments; polls every 30 s so new devices surface without a reload. */
export function useSignageEnrollments(status = 'pending') {
  return useQuery({
    queryKey: signageQueryKeys.enrollments(status),
    queryFn: () => signageClient.listEnrollments(status),
    refetchInterval: 30_000,
    staleTime: 15_000,
  });
}

export function useApproveEnrollment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ApproveEnrollmentDTO }) =>
      signageClient.approveEnrollment(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['signage', 'enrollments'] });
      qc.invalidateQueries({ queryKey: signageQueryKeys.displays });
    },
  });
}

export function useRejectEnrollment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => signageClient.rejectEnrollment(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['signage', 'enrollments'] });
    },
  });
}

// ─── Display management ──────────────────────────────────────────────────────

export function useUpdateSignageDisplay() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateDisplayDTO }) =>
      signageClient.updateDisplay(id, data),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: signageQueryKeys.displays }),
  });
}

export function useDeleteSignageDisplay() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => signageClient.deleteDisplay(id),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: signageQueryKeys.displays }),
  });
}

// ─── Backend version ─────────────────────────────────────────────────────────

/**
 * Backend version info from the public GET /api/v1/version endpoint.
 * Fetched once (long staleTime, no retry) — callers must render gracefully
 * when it errors (older backend without the endpoint, or unreachable).
 */
export function useBackendVersion(enabled = true) {
  return useQuery({
    queryKey: signageQueryKeys.backendVersion,
    queryFn: () => signageClient.getBackendVersion(),
    enabled,
    staleTime: Infinity,
    retry: false,
    refetchOnWindowFocus: false,
  });
}

export function useRestartDisplay() {
  return useMutation({
    mutationFn: (id: string) => signageClient.restartDisplay(id),
  });
}

export function useClearDisplayCache() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => signageClient.clearDisplayCache(id),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: signageQueryKeys.displays }),
  });
}

export function useDisplayDetail(id: string | null | undefined) {
  return useQuery({
    queryKey: signageQueryKeys.display(id ?? ''),
    queryFn: () => signageClient.getDisplay(id as string),
    enabled: Boolean(id),
    staleTime: 15_000,
  });
}

export function useDisplayCache(id: string | null | undefined) {
  return useQuery({
    queryKey: signageQueryKeys.displayCache(id ?? ''),
    queryFn: () => signageClient.getDisplayCache(id as string),
    enabled: Boolean(id),
    staleTime: 15_000,
  });
}

export function useUpdateDisplayConfig(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<SignageDisplay>) =>
      signageClient.updateDisplayConfig(id, data),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: signageQueryKeys.displays }),
  });
}

export type {
  BackendVersionInfo,
  SignageEnrollment,
  SignageBestsellers,
  SignageDaypartRule,
  SignageDaypartRuleInput,
  SignageDisplay,
  SignageAnnouncement,
  SignagePlaylist,
  SignagePlaylistItem,
  SignageMenuConfig,
  SignageMenuProduct,
  SignageDisplaySettings,
};
